/**
 * Users API wrappers (BUILD_SPEC.md §6.4).
 *
 * Mirrors the admin-only user-management surface. The handler-side
 * guards (cannot create ADMIN, cannot delete admin or self) are still
 * authoritative; UI helpers `canDeleteUser`/`canDeactivateUser` exist
 * solely to disable the buttons rather than letting the user click
 * into a 403.
 */

import { api } from "./client";
import type { User } from "./types";

export async function listUsers(): Promise<User[]> {
  const res = await api.get<{ items: User[] }>("/users");
  return res.data.items;
}

/**
 * Discriminated payload — EMPLOYEE requires an admin-typed password and
 * optionally a canEdit flag; DISPLAY (v1.1 B3 TV-kiosk role) only needs
 * a username and the server generates the password. ADMIN is rejected
 * server-side.
 */
export type CreateUserInput =
  | { role?: "EMPLOYEE"; username: string; password: string; canEdit?: boolean }
  | { role: "DISPLAY"; username: string };

/**
 * Response shape. `initialPassword` is only present in the 201 body for
 * DISPLAY creates — the SPA renders it once on a one-shot screen and
 * the server never exposes it again. EMPLOYEE creates return only the
 * sanitized `user` object.
 */
export interface CreateUserResponse {
  user: User;
  initialPassword?: string;
}

export async function createUser(input: CreateUserInput): Promise<CreateUserResponse> {
  const res = await api.post<CreateUserResponse>("/users", input);
  return res.data;
}

export async function updateUser(id: string, patch: { canEdit?: boolean; isActive?: boolean }): Promise<User> {
  const res = await api.put<{ user: User }>(`/users/${id}`, patch);
  return res.data.user;
}

export async function resetUserPassword(id: string, newPassword: string): Promise<void> {
  await api.post(`/users/${id}/reset-password`, { newPassword });
}

export async function deleteUser(id: string): Promise<void> {
  await api.delete(`/users/${id}`);
}

/**
 * UI guard — disables the delete button for the admin row and the
 * row representing the currently signed-in user. The server enforces
 * the same rule; this helper just keeps the user from clicking into
 * a guaranteed 403.
 */
export function canDeleteUser(target: User, currentUserId: string | null): boolean {
  if (target.role === "ADMIN") return false;
  if (currentUserId && target.id === currentUserId) return false;
  return true;
}

/** UI guard for the deactivate toggle — only blocks self-deactivation. */
export function canDeactivateUser(target: User, currentUserId: string | null): boolean {
  if (currentUserId && target.id === currentUserId && target.isActive) return false;
  return true;
}
